import type { HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { useCallback, useId, useMemo, useRef } from "react";
import { cx } from "../../utils/cx";
import styles from "./SegmentControl.module.scss";

export type SegmentControlItem = {
    id: string;
    label: ReactNode;
    disabled?: boolean;
};

export type SegmentControlProps = Omit<
    HTMLAttributes<HTMLDivElement>,
    "onChange" | "defaultValue"
> & {
    /** Two or more segments (Figma documents 2–5). */
    items: readonly SegmentControlItem[];
    /** Id of the selected segment (`items[].id`). */
    value: string;
    onValueChange: (id: string) => void;
    /** Disables the whole control. */
    disabled?: boolean;
    /** Passed to `radiogroup` — required for accessibility. */
    "aria-label": string;
};

function selectableIds(
    items: readonly SegmentControlItem[],
    allDisabled: boolean,
): string[] {
    if (allDisabled) {
        return [];
    }
    return items.filter(i => !i.disabled).map(i => i.id);
}

/**
 * Treez DS — SegmentControl (segmented control).
 *
 * Single selection from a horizontal set. Figma: node `7346:46316`
 * (`qgFv3OQga6RQ4luvSFuGFP`).
 *
 * Uses `role="radiogroup"` / `role="radio"` with Arrow Left/Right, Home, End.
 */
export function SegmentControl({
    items,
    value,
    onValueChange,
    disabled = false,
    className,
    "aria-label": ariaLabel,
    id,
    ...rest
}: SegmentControlProps) {
    const generatedId = useId();
    const groupId = id ?? `ds-segment-control-${generatedId}`;
    const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const ids = useMemo(
        () => selectableIds(items, disabled),
        [disabled, items],
    );

    const rovingTabId = useMemo(() => {
        if (ids.length === 0) {
            return "";
        }
        const selectedOk = items.some(
            i => i.id === value && !(disabled || i.disabled),
        );
        return selectedOk ? value : ids[0];
    }, [disabled, ids, items, value]);

    const setBtnRef = useCallback(
        (segmentId: string, el: HTMLButtonElement | null) => {
            if (el) {
                btnRefs.current[segmentId] = el;
            } else {
                delete btnRefs.current[segmentId];
            }
        },
        [],
    );

    const focusId = useCallback((segmentId: string) => {
        requestAnimationFrame(() => {
            btnRefs.current[segmentId]?.focus();
        });
    }, []);

    const moveSelection = useCallback(
        (fromId: string, delta: number) => {
            if (ids.length === 0) {
                return;
            }
            let idx = ids.indexOf(fromId);
            if (idx < 0) {
                idx = 0;
            }
            const nextIdx = (idx + delta + ids.length) % ids.length;
            const nextId = ids[nextIdx];
            if (nextId !== value) {
                onValueChange(nextId);
            }
            focusId(nextId);
        },
        [focusId, ids, onValueChange, value],
    );

    const handleGroupKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled || ids.length === 0) {
            return;
        }

        const { key } = event;
        if (
            key !== "ArrowRight" &&
            key !== "ArrowLeft" &&
            key !== "Home" &&
            key !== "End"
        ) {
            return;
        }
        event.preventDefault();

        const current = ids.includes(value) ? value : ids[0];

        if (key === "Home") {
            const first = ids[0];
            if (first !== value) {
                onValueChange(first);
            }
            focusId(first);
            return;
        }
        if (key === "End") {
            const last = ids[ids.length - 1];
            if (last !== value) {
                onValueChange(last);
            }
            focusId(last);
            return;
        }
        if (key === "ArrowRight") {
            moveSelection(current, 1);
            return;
        }
        moveSelection(current, -1);
    };

    return (
        <div
            aria-disabled={disabled || undefined}
            aria-label={ariaLabel}
            aria-orientation="horizontal"
            className={cx(styles.root, className)}
            data-disabled={disabled || undefined}
            data-ui="segment-control"
            id={groupId}
            onKeyDown={handleGroupKeyDown}
            role="radiogroup"
            {...rest}
        >
            {items.map(item => {
                const selected = item.id === value;
                const itemDisabled = Boolean(disabled || item.disabled);
                const tabIndex =
                    itemDisabled || !rovingTabId
                        ? -1
                        : item.id === rovingTabId
                        ? 0
                        : -1;

                return (
                    <button
                        aria-checked={selected}
                        className={styles.segment}
                        data-selected={selected ? "true" : undefined}
                        disabled={itemDisabled}
                        key={item.id}
                        onClick={() => {
                            if (!itemDisabled && item.id !== value) {
                                onValueChange(item.id);
                            }
                        }}
                        ref={el => setBtnRef(item.id, el)}
                        role="radio"
                        tabIndex={tabIndex}
                        type="button"
                    >
                        <span className={styles.segmentLabel}>
                            {item.label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

SegmentControl.displayName = "SegmentControl";
